import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
} from "@coreui/react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import Client, {
  CLIENT_TYPE_CHOICES,
  CLIENT_TYPE_DIPLOMATIC,
  CLIENT_TYPE_ENTITY,
  CLIENT_TYPE_FOREIGN_ENTITY,
  CLIENT_TYPE_GOVERNMENT,
  CLIENT_TYPE_NOT_NOMINATED,
  CLIENT_TYPE_PERSON,
  newClient,
} from "../../models/client";
import Errors, { getFieldErrors } from "../../models/errors";
import { RootState } from "../../store";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { ItemRequestStatus, createItem, updateItem } from "../../api/generics";
import { consultRuc } from "../../api/ruc";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import DepartmentSelect from "../location/DepartmentSelect";
import Department from "../../models/department";
import District from "../../models/district";
import City from "../../models/city";
import DistrictSelect from "../location/DistrictSelect";
import CitySelect from "../location/CitySelect";
import {
  getCityByCode,
  getDepartmentByCode,
  getDistrictByCode,
} from "../../api/locations";
import Loading from "../indicators/Loading";
import SelectBootstrap from "../react-select-bootstrap/SelectBootstrap";
import { COUNTRIES, Country } from "../../models/countries";
import "./client-form.scss";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "14px", marginTop: "14px" }}>
    <div style={{ width: "4px", height: "18px", background: "#006657", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "var(--theme-accent)" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; htmlFor?: string; children: React.ReactNode }> = ({ required, htmlFor, children }) => (
  <CLabel htmlFor={htmlFor} className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

interface ClientFormProps {
  client?: Client;
  onCancel: () => void | Promise<void>;
  onSuccess: (_?: Client) => void | Promise<void>;
  clearOnSuccess?: boolean;
}

const ClientForm: React.FC<ClientFormProps> = ({
  client,
  onCancel,
  onSuccess,
  clearOnSuccess,
}) => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer
  );

  const [loading, setLoading] = useState(false);

  // TODO: verify error with regular clearOnSuccess bool
  // eslint-disable-next-line
  const [shouldClearOnSuccess, _] = useState(clearOnSuccess);

  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [clientType, setClientType] = useState(
    client ? client.type : CLIENT_TYPE_PERSON
  );
  const [department, setDepartment] = useState<Department | null>(null);
  const [district, setDistrict] = useState<District | null>(null);
  const [city, setCity] = useState<City | null>(null);

  const [socialName, setSocialName] = useState("");
  const [ruc, setRuc] = useState(client?.ruc ?? "");
  const [consultingRuc, setConsultingRuc] = useState(false);
  const [rucConsultError, setRucConsultError] = useState("");
  const [rucConsultMessage, setRucConsultMessage] = useState("");
  const rucRequest = useRef(0);
  const [isNotNominated, setIsNotNominated] = useState(false);

  const clientRef = useRef<Client | null>(client ? { ...client } : null);

  const invalidateRucConsultation = () => {
    rucRequest.current += 1;
    setConsultingRuc(false);
    setRucConsultError("");
    setRucConsultMessage("");
  };

  useEffect(() => {
    invalidateRucConsultation();
    return () => { rucRequest.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, taxPayer.id]);

  const [hasAddress, setHasAddress] = useState(
    clientRef !== null && clientRef.current?.hasAddress ? true : false
  );

  const [isTaxPayer, setIsTaxPayer] = useState(
    clientRef !== null && clientRef.current?.isTaxPayer
      ? clientRef.current?.isTaxPayer
      : false
  );

  const initialCountry = COUNTRIES.find((el) => {
    let code = "PRY";
    if (
      clientRef !== null &&
      clientRef.current !== null &&
      clientRef.current.countryCode !== undefined
    ) {
      code = clientRef.current.countryCode;
    }

    return code === el.code;
  });

  const [country, setCountry] = useState<Country | null>(
    initialCountry !== undefined
      ? initialCountry
      : { code: "PRY", description: "Paraguay" }
  );

  const updateClientRef = async (client: Client) => {
    clientRef.current = client;
  };

  const onSocialNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }
    setSocialName(e.target.value);
    clientRef.current.socialName = e.target.value;
    invalidateRucConsultation();
  };

  const onFantasyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.fantasyName = e.target.value;
  };

  const onRucChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.ruc = e.target.value;
    setRuc(e.target.value);
    invalidateRucConsultation();
  };

  const onConsultRuc = async () => {
    const currentClient = clientRef.current;
    if (!currentClient || consultingRuc || submitting || taxPayer.id === undefined) return;
    invalidateRucConsultation();
    const requestedRuc = (currentClient.ruc ?? "").trim();
    if (!/^\d{5,8}(?:-\d)?$/.test(requestedRuc)) {
      setRucConsultError("Ingrese un RUC de 5 a 8 dígitos, con o sin dígito verificador.");
      return;
    }

    const request = rucRequest.current;
    setConsultingRuc(true);
    try {
      const response = await consultRuc(taxPayer.id, requestedRuc);
      if (request !== rucRequest.current || clientRef.current !== currentClient) return;
      if (response.status !== SUCCESS || !response.data) {
        setRucConsultError(response.detail || "No se pudo consultar el RUC. Intente nuevamente.");
        return;
      }
      const result = response.data;
      if (!result.found) {
        setRucConsultError(`No se encontró el RUC ${result.ruc} en SIFEN.`);
        return;
      }
      if (!result.businessName) {
        setRucConsultError("SIFEN no devolvió el nombre del contribuyente. Intente nuevamente.");
        return;
      }

      currentClient.socialName = result.businessName;
      currentClient.ruc = requestedRuc;
      currentClient.isTaxPayer = true;
      setSocialName(result.businessName);
      setRuc(requestedRuc);
      setIsTaxPayer(true);
      setErrors((previous) => {
        const next = { ...previous };
        delete next.socialName;
        delete next.isTaxPayer;
        return next;
      });
      setRucConsultMessage(
        `Datos completados. Estado del RUC: ${result.statusDescription}. ` +
        `Facturador electrónico: ${result.electronicInvoicing ? "Sí" : "No"}. ` +
        (requestedRuc.includes("-") ? "Revise los datos antes de guardar." : "Complete el dígito verificador antes de guardar."),
      );
    } catch {
      if (request === rucRequest.current) {
        setRucConsultError("No se pudo consultar el RUC. Revise su conexión e intente nuevamente.");
      }
    } finally {
      if (request === rucRequest.current) setConsultingRuc(false);
    }
  };

  const onIsTaxPayerChange = (isTaxPayer: boolean) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.isTaxPayer = isTaxPayer;
    setIsTaxPayer(isTaxPayer);
  };

  const onHasAddressChange = (hasAddress: boolean) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.hasAddress = hasAddress;
    if (!hasAddress) {
      setDepartment(null);
      setDistrict(null);
      setCity(null);
      clientRef.current.address = undefined;
      clientRef.current.houseNumber = undefined;
    }
    setHasAddress(hasAddress);
  };

  const onAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }
    clientRef.current.address = e.target.value;
  };

  const onCountryChange = (
    newCountry: {
      code: string;
      description: string;
    } | null
  ) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.countryCode =
      newCountry !== null ? newCountry.code : undefined;
    setCountry(newCountry);
  };

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.email = e.target.value;
  };

  const onPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.phoneNumber = e.target.value;
  };

  const onHouseNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }
    if (e.target.value === undefined) {
      clientRef.current.houseNumber = undefined;
    } else {
      clientRef.current.houseNumber = Number(e.target.value);
    }
  };

  const onClientTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (clientRef.current === null) {
      return;
    }

    const newClientType = e.target.value;
    invalidateRucConsultation();

    setClientType(newClientType);

    clientRef.current.type = e.target.value;

    if (newClientType === CLIENT_TYPE_FOREIGN_ENTITY) {
      setHasAddress(true);
      setIsTaxPayer(false);
      setDepartment(null);
      setDistrict(null);
      setCity(null);
      setCountry(null);
      clientRef.current.countryCode = undefined;
      clientRef.current.hasAddress = true;
      clientRef.current.isTaxPayer = false;
      clientRef.current.city = undefined;
      clientRef.current.cityDescription = undefined;
      clientRef.current.district = undefined;
      clientRef.current.districtDescription = undefined;
      clientRef.current.department = undefined;
      clientRef.current.departmentDescription = undefined;
      clientRef.current.houseNumber = undefined;
    } else if (newClientType === CLIENT_TYPE_DIPLOMATIC) {
      setIsTaxPayer(false);
    } else {
      setCountry({ code: "PRY", description: "Paraguay" });
      clientRef.current.countryCode = "PRY";
    }

    if (newClientType === CLIENT_TYPE_NOT_NOMINATED) {
      clientRef.current.socialName = "Sin Nombre";
      clientRef.current.isTaxPayer = false;
      setIsTaxPayer(false);
      setIsNotNominated(true);
      setSocialName("Sin Nombre");
    } else {
      clientRef.current.socialName = "";
      setIsNotNominated(false);
      setSocialName("");
    }
  };

  const onDepartmentChange = (newDepartment: Department | null) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.department = newDepartment?.code;
    clientRef.current.departmentDescription = newDepartment?.description;
    clientRef.current.district = undefined;
    clientRef.current.districtDescription = undefined;

    setDepartment(newDepartment);
    setDistrict(null);
    setCity(null);
  };

  const onDistrictChange = (newDistrict: District | null) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.district = newDistrict?.code;
    clientRef.current.districtDescription = newDistrict?.description;
    clientRef.current.city = undefined;
    clientRef.current.cityDescription = undefined;

    setDistrict(newDistrict);
    setCity(null);
  };

  const onCityChange = (newCity: City | null) => {
    if (clientRef.current === null) {
      return;
    }

    clientRef.current.city = newCity?.code;
    clientRef.current.cityDescription = newCity?.description;

    setCity(newCity);
  };

  const onSave = async () => {
    const client = clientRef.current;

    if (client === null || consultingRuc) {
      return;
    }

    setSubmitting(true);

    client.taxPayerId = taxPayer.id;

    if (client.isTaxPayer === undefined) {
      client.isTaxPayer = false;
    }
    if (client.type === CLIENT_TYPE_PERSON) {
      client.fantasyName = undefined;
    }
    if (!hasAddress) {
      client.city = undefined;
      client.cityDescription = undefined;
      client.district = undefined;
      client.districtDescription = undefined;
      client.department = undefined;
      client.departmentDescription = undefined;
      client.address = undefined;
      client.houseNumber = undefined;
    }

    if (client.type === CLIENT_TYPE_FOREIGN_ENTITY) {
      client.city = undefined;
      client.cityDescription = undefined;
      client.district = undefined;
      client.districtDescription = undefined;
      client.department = undefined;
      client.departmentDescription = undefined;
      client.houseNumber = undefined;
    } else {
      if (client.countryCode === undefined) {
        client.countryCode = "PRY";
      }
    }

    let requestPromise: Promise<ItemRequestStatus<Client>>;
    if (client.id === undefined) {
      requestPromise = createItem<Client>("/clients/create/", client);
    } else {
      requestPromise = updateItem(`/clients/${client.id}/`, client);
    }

    const clientStatus = await requestPromise;

    if (clientStatus.status !== SUCCESS) {
      if (clientStatus.errors !== undefined) {
        setErrors(clientStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (clientStatus.detail !== undefined) {
        message = clientStatus.detail;
      }
      errorAlert(message);
      setSubmitting(false);
    } else {
      if (shouldClearOnSuccess) {
        setErrors({});
        clearForm();
      }
      onSuccess(clientStatus.data);
    }

    if (shouldClearOnSuccess) {
      setSubmitting(false);
    }
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    invalidateRucConsultation();
    setRuc("");
    setSocialName("");
    setIsTaxPayer(false);
    updateClientRef({ ...newClient(taxPayer.id), type: clientType });
  };

  const loadClient = async (client?: Client) => {
    invalidateRucConsultation();
    setLoading(true);
    if (client !== undefined) {
      if (client.department !== undefined) {
        const updatingDeparment = await getDepartmentByCode(client.department);
        setDepartment(updatingDeparment);
      }

      if (client.district !== undefined) {
        const updatingDistrict = await getDistrictByCode(client.district);
        setDistrict(updatingDistrict);
      }

      if (client.city !== undefined) {
        const updatingCity = await getCityByCode(client.city);
        setCity(updatingCity);
      }

      setSocialName(client.socialName !== undefined ? client.socialName : "");

      if (client.type === CLIENT_TYPE_NOT_NOMINATED) {
        setIsNotNominated(true);
      }
    } else {
      setSocialName("");
    }

    setRuc(client?.ruc ?? "");
    const nextClient = {
      ...(client ?? newClient(taxPayer.id)),
      type: client?.type ?? CLIENT_TYPE_PERSON,
    };
    setClientType(nextClient.type);
    setIsTaxPayer(Boolean(nextClient.isTaxPayer));
    setIsNotNominated(nextClient.type === CLIENT_TYPE_NOT_NOMINATED);
    updateClientRef(nextClient);
    setLoading(false);
  };

  useEffect(() => {
    loadClient(client);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  let socialNamePlaceholder = "Nombre de la persona o empresa";
  let rucLabel = "RUC o CI";
  let rucPlaceholder = "RUC con dígito verificador";

  if (clientType === CLIENT_TYPE_PERSON) {
    socialNamePlaceholder = "Nombre de la persona";
  } else if (clientType === CLIENT_TYPE_DIPLOMATIC) {
    socialNamePlaceholder = "Nombre de la persona";
    rucLabel = "Número de tarjeta diplomática";
    rucPlaceholder = "Número de tarjeta diplomática";
  } else if (clientType === CLIENT_TYPE_ENTITY) {
    socialNamePlaceholder = "Nombre de la Empresa";
    rucLabel = "RUC";
  } else if (clientType === CLIENT_TYPE_GOVERNMENT) {
    socialNamePlaceholder = "Nombre de la Entidad";
    rucLabel = "RUC";
  }

  const showFantasyName = clientType === CLIENT_TYPE_ENTITY;
  const canConsultRuc = [CLIENT_TYPE_PERSON, CLIENT_TYPE_ENTITY, CLIENT_TYPE_GOVERNMENT].includes(clientType ?? "");

  if (loading) {
    return <Loading height="30vh" />;
  }

  return (
    <>
      <fieldset disabled={submitting}>
        <SectionDivider icon="fa-user" label="Datos Generales" />
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel required>Tipo:</FormLabel>
            </CCol>
            <CCol>
              <CSelect
                type="text"
                disabled={consultingRuc}
                value={clientType}
                onChange={onClientTypeChange}
              >
                <option value={""} disabled>
                  -----
                </option>
                {Array.from(CLIENT_TYPE_CHOICES.entries()).map((entry) => {
                  return (
                    <option key={entry[0]} value={entry[0]}>
                      {entry[1]}
                    </option>
                  );
                })}
              </CSelect>
              <FieldErrors
                errors={getFieldErrors("type", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel required htmlFor="client-social-name">Nombre:</FormLabel>
            </CCol>
            <CCol>
              <CInput
                id="client-social-name"
                type="text"
                value={socialName}
                onChange={onSocialNameChange}
                disabled={isNotNominated || consultingRuc}
                placeholder={socialNamePlaceholder}
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("socialName", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {showFantasyName ? (
          <CFormGroup>
            <CRow className="align-items-center">
              <CCol md={3}>
                <FormLabel>Nombre de Fantasía:</FormLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  defaultValue={
                    clientRef.current ? clientRef.current.fantasyName : ""
                  }
                  onChange={onFantasyNameChange}
                  placeholder="Nombre de Fantasia"
                ></CInput>
                <FieldErrors
                  errors={getFieldErrors("fantasyName", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </CRow>
          </CFormGroup>
        ) : (
          <></>
        )}

        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel required htmlFor="client-ruc">{rucLabel}</FormLabel>
            </CCol>
            <CCol>
              <div className="client-ruc-lookup">
                <CInput
                  id="client-ruc"
                  type="text"
                  value={ruc}
                  onChange={onRucChange}
                  placeholder={rucPlaceholder}
                  disabled={consultingRuc}
                  aria-describedby={canConsultRuc ? "client-ruc-lookup-help" : undefined}
                />
                {canConsultRuc && (
                  <CButton
                    type="button"
                    color="primary"
                    variant="outline"
                    onClick={onConsultRuc}
                    disabled={consultingRuc || taxPayer.id === undefined}
                  >
                    {consultingRuc && <Spinner animation="border" size="sm" className="mr-2" aria-hidden="true" />}
                    {consultingRuc ? "Consultando..." : "Consulta RUC"}
                  </CButton>
                )}
              </div>
              {canConsultRuc && (
                <small id="client-ruc-lookup-help" className="form-text text-muted">
                  Complete el nombre y la condición de contribuyente desde SIFEN.
                </small>
              )}
              {rucConsultError && <div className="alert alert-danger mt-2 mb-0" role="alert">{rucConsultError}</div>}
              {rucConsultMessage && <div className="alert alert-info mt-2 mb-0" role="status">{rucConsultMessage}</div>}
              <FieldErrors
                errors={getFieldErrors("ruc", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel>Es contribuyente</FormLabel>
            </CCol>
            <CCol>
              <CRow>
                <AnimatedCheckbox
                  value={isTaxPayer}
                  onChange={onIsTaxPayerChange}
                  disabled={
                    consultingRuc ||
                    clientType === CLIENT_TYPE_FOREIGN_ENTITY ||
                    clientType === CLIENT_TYPE_NOT_NOMINATED ||
                    clientType === CLIENT_TYPE_DIPLOMATIC
                  }
                ></AnimatedCheckbox>
              </CRow>

              <FieldErrors
                errors={getFieldErrors("isTaxPayer", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel>Agregar datos de dirección</FormLabel>
            </CCol>
            <CCol>
              <CRow>
                <AnimatedCheckbox
                  value={hasAddress}
                  disabled={clientType === CLIENT_TYPE_FOREIGN_ENTITY}
                  onChange={onHasAddressChange}
                ></AnimatedCheckbox>
              </CRow>

              <FieldErrors
                errors={getFieldErrors("isTaxPayer", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {hasAddress ? (
          <>
            <SectionDivider icon="fa-map-marker" label="Dirección" />
            {clientType === CLIENT_TYPE_FOREIGN_ENTITY ? (
              <>
                <CFormGroup>
                  <CRow className="align-items-center">
                    <CCol md={3}>
                      <FormLabel required>País</FormLabel>
                    </CCol>
                    <CCol>
                      <SelectBootstrap
                        placeholder="Seleccione un País"
                        options={COUNTRIES}
                        value={country}
                        getOptionValue={(country) => country.code}
                        getOptionLabel={(country) => country.description}
                        onChange={onCountryChange}
                      ></SelectBootstrap>
                    </CCol>
                  </CRow>
                </CFormGroup>
              </>
            ) : (
              <></>
            )}
            <CFormGroup></CFormGroup>
            {clientType !== CLIENT_TYPE_FOREIGN_ENTITY ? (
              <>
                <CFormGroup>
                  <CRow className="align-items-center">
                    <CCol md={3}>
                      <FormLabel>Departamento</FormLabel>
                    </CCol>
                    <CCol>
                      <DepartmentSelect
                        onChange={onDepartmentChange}
                        value={department}
                      />
                      <FieldErrors
                        errors={
                          getFieldErrors("department", errors) as string[]
                        }
                      ></FieldErrors>
                    </CCol>
                  </CRow>
                </CFormGroup>
                <CFormGroup>
                  <CRow className="align-items-center">
                    <CCol md={3}>
                      <FormLabel>Distrito</FormLabel>
                    </CCol>
                    <CCol>
                      <DistrictSelect
                        onChange={onDistrictChange}
                        value={district}
                        departmentValue={
                          department !== null ? department : undefined
                        }
                      />
                      <FieldErrors
                        errors={getFieldErrors("district", errors) as string[]}
                      ></FieldErrors>
                    </CCol>
                  </CRow>
                </CFormGroup>
                <CFormGroup>
                  <CRow className="align-items-center">
                    <CCol md={3}>
                      <FormLabel>Ciudad</FormLabel>
                    </CCol>
                    <CCol>
                      <CitySelect
                        onChange={onCityChange}
                        value={city}
                        district={district !== null ? district : undefined}
                        department={
                          department !== null ? department : undefined
                        }
                      />
                      <FieldErrors
                        errors={getFieldErrors("city", errors) as string[]}
                      ></FieldErrors>
                    </CCol>
                  </CRow>
                </CFormGroup>
              </>
            ) : (
              <></>
            )}

            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={3}>
                  <FormLabel>Dirección</FormLabel>
                </CCol>
                <CCol>
                  <CInput
                    type="text"
                    defaultValue={
                      clientRef.current ? clientRef.current.address : ""
                    }
                    onChange={onAddressChange}
                    placeholder="Dirección"
                  ></CInput>
                  <FieldErrors
                    errors={getFieldErrors("address", errors) as string[]}
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            {clientType !== CLIENT_TYPE_FOREIGN_ENTITY ? (
              <CFormGroup>
                <CRow className="align-items-center">
                  <CCol md={3}>
                    <FormLabel>Número de casa:</FormLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="number"
                      defaultValue={
                        clientRef.current
                          ? clientRef.current.houseNumber
                          : undefined
                      }
                      onChange={onHouseNumberChange}
                      placeholder="Número de casa"
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("houseNumber", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
            ) : (
              <></>
            )}
          </>
        ) : (
          <></>
        )}
        <SectionDivider icon="fa-phone" label="Contacto" />
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel>Teléfono</FormLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                defaultValue={
                  clientRef.current ? clientRef.current.phoneNumber : ""
                }
                onChange={onPhoneNumberChange}
                placeholder="Número de teléfono"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("phoneNumber", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={3}>
              <FormLabel>Email</FormLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                defaultValue={clientRef.current ? clientRef.current.email : ""}
                onChange={onEmailChange}
                placeholder="Dirección de email"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("email", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton
              type="button"
              color="secondary"
              onClick={onClose}
              style={{ fontWeight: 600 }}
            >
              <i className="fa fa-times mr-2"></i>
              Salir
            </CButton>
            <CButton
              type="submit"
              color="primary"
              disabled={consultingRuc}
              onClick={onSave}
              style={{ fontWeight: 600, minWidth: 120 }}
            >
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <i className="fa fa-save mr-2"></i>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default ClientForm;
